<template>
  <div>
    <v-dialog v-model="show" scrollable max-width="1600px">
      <v-card>
        <v-card-text class="pt-6">
          <v-row>
            <v-col cols="6" lg="4">
              <v-img max-width="400px" :src="getImagePath(image)" class="grey lighten-2" contain></v-img>
            </v-col>
            <v-col cols="6" lg="8" class="pl-5">
              <v-alert :value="isRejected" type="warning" outlined>
                <h3>{{ $t('components.dialog.imageEditDetails.message') }}</h3>
                <span class="text-with-linebreak">{{ image.reject_comment }}</span>
              </v-alert>

              <v-alert :value="editMode && isApproved" type="info" outlined>
                <h3>{{ $t('components.dialog.imageEditDetails.note') }}</h3>
                <span>{{ $t('components.dialog.imageEditDetails.editWarningText') }}</span>
              </v-alert>

              <h3>{{ $t('components.dialog.imageEditDetails.description') }}</h3>
              <div class="mb-2" v-if="!editMode">
                <span class="text-with-linebreak">{{ localeDescription }}</span>
              </div>
              <div v-if="editMode">
                <v-tabs v-model="descriptionTab">
                  <v-tab>{{ $t('components.dialog.imageEditDetails.swedish') }}</v-tab>
                  <v-tab>{{ $t('components.dialog.imageEditDetails.english') }}</v-tab>
                </v-tabs>
                <v-tabs-items v-model="descriptionTab">
                  <v-tab-item>
                    <v-textarea v-model="editImageObject.description" name="description" class="pt-0"></v-textarea>
                  </v-tab-item>
                  <v-tab-item>
                    <v-textarea
                      v-model="editImageObject.description_eng"
                      name="description_eng"
                      class="pt-0"
                    ></v-textarea>
                  </v-tab-item>
                </v-tabs-items>
              </div>

              <h3>{{ $t('components.dialog.imageEditDetails.category') }}</h3>
              <p v-if="!editMode">{{ image.categories?.map((c) => pickLocaleDescriptionValue(c)).join(', ') }}</p>
              <category-selector-component v-if="editMode" v-model="selectedCategory"></category-selector-component>

              <h3 v-if="editMode">{{ $t('components.dialog.imageEditDetails.tags') }}</h3>
              <TagAsObject
                v-if="editMode"
                ref="mytag2"
                @add="tagAdded($event)"
                @remove="tagRemoved($event)"
              ></TagAsObject>

              <h3>{{ $t('components.dialog.imageEditDetails.color') }}</h3>
              <p v-if="!editMode">{{ image.colors?.map((c) => pickLocaleTextValue(c)).join(', ') }}</p>
              <v-combobox
                v-if="editMode"
                multiple
                small-chips
                :label="$t('components.dialog.imageEditDetails.color')"
                v-model="editImageObject.colors"
                :items="lookupStore.colors"
              ></v-combobox>

              <h3>{{ $t('components.dialog.imageEditDetails.typeOfImage') }}</h3>
              <p v-if="!editMode">
                {{
                  image.type === 1
                    ? $t('components.dialog.imageEditDetails.environmentImage')
                    : $t('components.dialog.imageEditDetails.productImage')
                }}
              </p>
              <v-radio-group v-if="editMode" v-model="editImageObject.type" class="mt-1">
                <v-radio :label="$t('components.dialog.imageEditDetails.environmentImage')" :value="1"></v-radio>
                <v-radio :label="$t('components.dialog.imageEditDetails.productImage')" :value="2"></v-radio>
              </v-radio-group>

              <h3 v-if="!editMode && image.is_new_product">
                {{ $t('components.dialog.imageEditDetails.newProduct') }}
              </h3>
              <div class="mb-2" v-if="!editMode && image.release_date">
                {{ $t('components.dialog.imageEditDetails.launchDate') }}: {{ image.release_date }}
              </div>

              <v-checkbox
                v-if="editMode"
                :label="$t('components.dialog.imageEditDetails.productIsNew')"
                v-model="editImageObject.is_new_product"
                @change="$event === false ? (editImageObject.release_date = undefined) : null"
                :hide-details="true"
              ></v-checkbox>

              <v-menu
                v-if="editMode"
                v-model="releaseDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-if="editMode"
                    :disabled="!editImageObject.is_new_product"
                    v-model="editImageObject.release_date"
                    :label="$t('components.dialog.imageEditDetails.selectLaunchDate')"
                    readonly
                    v-on="on"
                    class="mb-3"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="editImageObject.release_date" @input="releaseDateMenu = false"></v-date-picker>
              </v-menu>

              <h3>{{ $t('components.dialog.imageEditDetails.season') }}</h3>
              <div class="mb-2" v-if="!editMode">
                {{ seasonText ? seasonText : '--' }}
              </div>
              <v-select
                v-if="editMode"
                :items="lookupStore.seasons"
                item-text="text"
                item-value="id"
                v-model="editImageObject.season"
                return-object
                class="pt-0"
              ></v-select>

              <h3>{{ $t('components.dialog.imageEditDetails.price') }}</h3>
              <div class="mb-2" v-if="!editMode">
                {{ localePrice }}
              </div>
              <v-text-field
                v-if="editMode"
                :label="$t('components.dialog.imageEditDetails.swedishPrice')"
                v-model="editImageObject.price"
                class="pt-0"
              ></v-text-field>
              <v-text-field
                v-if="editMode"
                :label="$t('components.dialog.imageEditDetails.englishPrice')"
                v-model="editImageObject.price_eng"
                class="pt-0"
              ></v-text-field>

              <h3>{{ $t('components.dialog.imageEditDetails.externalUrl') }}</h3>
              <div class="mb-2" v-html="externalUrl" v-if="!editMode"></div>
              <v-text-field
                v-if="editMode"
                v-model="editImageObject.external_url"
                name="external_url"
                class="pt-0"
              ></v-text-field>

              <h3 v-if="!editMode">{{ $t('components.dialog.imageEditDetails.company') }}</h3>
              <div class="mb-2" v-if="!editMode">
                {{ companyName }}<br />
                {{ $t('components.dialog.imageEditDetails.phone') }}: {{ companyPhone ? companyPhone : '--' }}<br />
                {{ $t('components.dialog.imageEditDetails.email') }}: {{ companyEmail ? companyEmail : '--' }}
              </div>

              <h3 v-if="!editMode">{{ $t('components.dialog.imageEditDetails.imageInformation') }}</h3>
              <div class="mb-2" v-if="!editMode">
                {{ $t('components.dialog.imageEditDetails.dimensions') }}: {{ image.width }} x {{ image.height }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pr-4 pb-3">
          <v-spacer></v-spacer>

          <v-btn v-if="!editMode" text @click="close()">
            {{ $t('common.close') }}
          </v-btn>

          <v-btn v-if="!editMode" color="error" outlined text @click="showConfirmDelete = true">
            {{ $t('common.delete') }}
          </v-btn>

          <v-btn v-if="!editMode" color="primary" class="black--text" @click="editImage(image)">
            {{ $t('common.edit') }}
          </v-btn>

          <v-btn v-if="editMode" text @click="cancelEditMode()">
            {{ $t('common.cancel') }}
          </v-btn>

          <v-btn v-if="editMode" color="success" outlined text :disabled="!isValid()" @click="saveImage()">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showConfirmDelete" persistent max-width="350">
      <v-card>
        <v-card-title class="headline">{{ $t('components.dialog.imageEditDetails.confirmDeleteTitle') }}</v-card-title>
        <v-card-text>
          <h3>{{ $t('components.dialog.imageEditDetails.confirmDeleteText') }}</h3>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="onCancelConfirmDialog">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="error" outlined text @click="deleteImage()">
            {{ $t('components.dialog.imageEditDetails.confirmDeleteButtonText') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import tags from '../common/TagComponent.vue';
import CategorySelectorComponent from '../common/CategorySelectorComponent.vue';
import TagAsObject from '../../components/common/TagAsObjectComponent.vue';
import { FileService } from '../../services/file.service';
import { mapStores } from 'pinia';
import { useLookupStore } from '../../stores/LookupStore';
import { pickLocaleText, pickLocaleDescription, pickLocaleName, isSwedishLocale } from '../../services/locale.service';

export default {
  components: {
    tags,
    TagAsObject,
    CategorySelectorComponent,
  },

  props: {
    visible: Boolean,
    mode: String,
    image: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },

  model: {
    prop: 'visible',
    event: 'update',
  },

  watch: {
    mode: function (newValue, oldValue) {
      if (newValue === 'edit') {
        this.editImage();
      }

      if (newValue === 'delete') {
        this.$nextTick(() => {
          this.showConfirmDelete = true;
        });
      }
    },
  },

  data() {
    return {
      editMode: false,
      editImageObject: undefined,
      fileService: undefined,
      showConfirmDelete: false,
      releaseDateMenu: false,
      descriptionTab: 0,
    };
  },

  computed: {
    ...mapStores(useLookupStore),

    show: {
      get: function () {
        return this.visible;
      },
      set: function (value) {
        this.$emit('update', value);

        if (value === false) {
          this.cancelEditMode();
          this.$emit('close');
        }
      },
    },

    isRejected: function () {
      return this.image && this.image.status && this.image.status.id === 4;
    },

    isApproved: function () {
      return this.image && this.image.status && (this.image.status.id === 3 || this.image.status.id === 6);
    },

    companyName: function () {
      return this.image && this.image.company ? this.image.company.name : '';
    },

    companyPhone: function () {
      return this.image && this.image.company ? this.image.company.phone : '';
    },

    companyEmail: function () {
      return this.image && this.image.company ? this.image.company.email : '';
    },

    seasonText: function () {
      return pickLocaleText(this.image?.season);
    },

    externalUrl: function () {
      const externalUrlText = this.$i18n.t('components.dialog.imageEditDetails.clickHereToOpenExternalUrl');
      return this.image && this.image.external_url
        ? `<a href="${this.image.external_url}" target="_blank">${externalUrlText}</a>`
        : ' -- ';
    },

    selectedCategory: {
      get: function () {
        return this.editImageObject?.categories[0];
      },
      set: function (value) {
        this.editImageObject.categories = [value];
      },
    },

    localeDescription() {
      const swedishDescription = this.image?.description;
      const englishDescription = this.image?.description_eng;

      if (!swedishDescription && !englishDescription) {
        return '--';
      }

      if (isSwedishLocale()) {
        return swedishDescription || englishDescription;
      }
      // pick swedish description if english is not available, so that we always have a description
      return englishDescription || swedishDescription;
    },

    localePrice() {
      const swedishPrice = this.image?.price;
      const englishPrice = this.image?.price_eng;

      if (!swedishPrice && !englishPrice) {
        return '--';
      }

      if (isSwedishLocale()) {
        return swedishPrice || englishPrice;
      }
      // pick swedish price if english is not available, so that we always have a price
      return englishPrice || swedishPrice;
    },
  },

  created() {
    this.lookupStore.loadSeasons();

    this.fileService = new FileService();
  },

  methods: {
    getImagePath(image) {
      if (!image.thumb_name) {
        return '';
      }
      return image.path + '/' + image.thumb_name;
    },

    close() {
      this.show = false;
    },

    editImage() {
      this.editMode = true;
      this.editImageObject = JSON.parse(JSON.stringify(this.image)); // clone image object
      this.editImageObject.colors = (this.editImageObject.colors || []).map((t) =>
        t ? { ...t, text: pickLocaleText(t) } : t
      );

      // tags
      this.$nextTick(() => {
        this.$refs.mytag2.setTags(this.editImageObject.tags);
      });
    },

    deleteImage() {
      this.showConfirmDelete = false;

      this.fileService
        .delete(this.image)
        .then(() => {
          this.show = false;
          this.$emit('deleted');
        })
        .catch((error) => {
          console.error('Fel vid borttagning!', error);
        });
    },

    isValid() {
      return this.editImageObject.tags.length > 0;
    },

    saveImage() {
      this.fileService
        .save([this.editImageObject])
        .then(() => {
          this.$emit('updated', this.editImageObject);
          this.close();
        })
        .catch((error) => {
          console.error('Fel vid sparandet!', error);
        });
    },

    cancelEditMode() {
      this.showConfirmDelete = false;
      this.editMode = false;
      this.editImageObject = undefined;
    },

    onCancelConfirmDialog() {
      this.showConfirmDelete = false;
    },

    tagAdded(tag) {
      if (this.editImageObject.tags.indexOf(tag) < 0) {
        this.editImageObject.tags.push(tag);
      }
    },

    tagRemoved(tag) {
      this.editImageObject.tags.splice(this.editImageObject.tags.indexOf(tag), 1);
    },

    pickLocaleDescriptionValue(item, defaultText = '') {
      return pickLocaleDescription(item, defaultText);
    },

    pickLocaleTextValue(item, defaultText = '') {
      return pickLocaleText(item, defaultText);
    },
  },
};
</script>

<style scoped>
.image {
  max-width: 400px;
  max-height: 400px;
}

.text-with-linebreak {
  white-space: pre-line;
}
</style>
